import type { AuditEntry } from "@spf/contracts";
import { useAuditLog } from "../lib/usePages.js";
import { formatDateTime, formatRelativeTime } from "../lib/format.js";
import { Empty, Failed, LoadOlder, Loading } from "../components/ViewState.js";
import { PageSection } from "../components/Page.js";

export function AuditPage() {
  const { state, loadingMore, moreFailed, reload, loadMore } = useAuditLog();

  return (
    <PageSection title="Activity">
      <p className="mb-4 text-sm text-ink-muted">
        Sign-ins, sign-outs and security events, newest first. This log is append-only.
      </p>

      {state.kind === "loading" && <Loading label="Loading activity…" />}
      {state.kind === "error" && <Failed message={state.message} onRetry={reload} />}
      {state.kind === "ready" && state.items.length === 0 && <Empty>Nothing recorded yet.</Empty>}
      {state.kind === "ready" && state.items.length > 0 && (
        <>
          <ol className="flex flex-col gap-3">
            {state.items.map((entry) => (
              <AuditRow key={entry.id} entry={entry} />
            ))}
          </ol>
          {state.nextBefore !== null && (
            <div className="mt-4">
              <LoadOlder
                loading={loadingMore}
                failed={moreFailed}
                failedMessage="Could not load older entries. Try again."
                onLoad={loadMore}
              />
            </div>
          )}
        </>
      )}
    </PageSection>
  );
}

function describe(detail: Record<string, unknown>): string | null {
  const parts = Object.entries(detail).map(([key, value]) => `${key}: ${String(value)}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const detail = describe(entry.detail);
  return (
    <li className="rounded-2xl border border-line p-4">
      <p className="text-sm font-medium text-ink">{entry.action}</p>
      <p className="mt-1 text-xs text-ink-muted">
        <time dateTime={entry.at} title={formatDateTime(entry.at)}>
          {formatRelativeTime(entry.at)}
        </time>
        {" · "}
        {entry.actorEmail ?? "unknown account"}
        {entry.ip !== null && ` · ${entry.ip}`}
      </p>
      {detail !== null && <p className="mt-1 text-xs text-ink-muted">{detail}</p>}
    </li>
  );
}
